import type { ReactNode } from 'react';
import type { AccessLevel, ModuleKey } from '@gty/common';

import { Forbidden } from '../pages/Forbidden';
import { PageSkeleton } from '../ui/primitives';
import { useAuth } from './useAuth';
import { usePermissions } from './usePermissions';

export interface RequirePermissionProps {
  module: ModuleKey;
  level: AccessLevel;
  children: ReactNode;
}

/**
 * Module-level gate.
 *
 * It RENDERS 403 rather than redirecting. A silent bounce to /dashboard makes a
 * deliberately typed URL look like a broken link and hides the actual reason —
 * the user needs to know they lack access, not wonder why navigation failed.
 *
 * Additive only: the server enforces the same rule on the data behind this
 * route. See `usePermissions` for why that distinction matters.
 */
export function RequirePermission({ module, level, children }: RequirePermissionProps): JSX.Element {
  const { status } = useAuth();
  const { allows } = usePermissions();

  // The permission map arrives with /auth/me. Until it does, render neither the
  // child nor Forbidden — both would be a guess, and one of them flashes
  // content the user may not be allowed to see.
  if (status === 'booting') {
    return <PageSkeleton />;
  }

  if (!allows(module, level)) {
    return <Forbidden module={module} requiredLevel={level} />;
  }

  return <>{children}</>;
}
